import React, { useEffect, useRef, useState } from 'react';
import { graphql, Link, Script } from 'gatsby';
import {
  GatsbyImage, getImage, IGatsbyImageData, StaticImage,
} from 'gatsby-plugin-image';
import Chevron from 'assets/icons/chevron-right.svg';
import { addMainAds } from 'helpers/add-main-ads';
import { Card, CardType } from 'components/people-card/card';
import { Layout } from 'components/layout';
import { formatDateToString } from 'utils/formatDateToString';
import { generateIndexedContent } from 'components/indexed-content/indexed-content';
import Indexes from 'components/indexed-content/indexes';
import { RelatedCard } from 'components/related-card/related-card';
import { parseProductHighlight, ProductType } from 'components/product-cards';
import { SourceCite } from 'components/source-cite';
import 'styles/global.scss';
import {
  classnames,
  createArticleUrl,
  createAuthorsUrl,
  createReviewersUrl,
  createYoutubeUrl,
  getFullName,
  getVideoSrcDoc,
  getYoutubeId,
  isWindowDefined,
} from 'helpers/utils';
import { CiteType, GatsbyImageType, SocialMediaType } from 'types/common';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs/breadcrumbs';
import { ContributorTypeEnum } from 'utils/enums';
import { SEO } from 'components/seo';
import {
  ArticleSEO,
  BreadcrumbSEO,
  createArticlesJSONLD,
  createBreadCrumbJSONLD,
  getAbsoluteUrl,
} from 'helpers/seo-helpers';
import * as styles from './blog-article.module.scss';
import { SmartLink } from 'components/smart-link';

type BlogsArticleProps = {
  pageContext: {
    slug: string
    cites: CiteType[]
    categoryId: string
    content: {
      data: {
        content: string
      }
    }
    subscribeBannerName:string
    subscribeBannerLink:string
  }
  data: {
    strapiArticle: {
      title: string
      author: {
        nameFirst: string
        nameLast: string
        namePrefix: string
        nameSuffix: string
        slug: string
        contributorType: ContributorTypeEnum
        avatar: GatsbyImageType
        socialMedia: SocialMediaType
        occupation: string
        shortDescription: string
      }
      reviewer?: {
        nameFirst: string
        nameLast: string
        namePrefix: string
        nameSuffix: string
        slug: string
        contributorType: ContributorTypeEnum
        avatar: GatsbyImageType
        socialMedia: SocialMediaType
        shortDescription: string
      }
      primaryImage?: {
        url: string
        localFile: {
          childImageSharp: {
            gatsbyImageData: IGatsbyImageData
          }
        }
      }
      productList: ProductType[]
      medicalReviewDate: string
      sponsoredText: {
        text: {
          data: {
            text: string
          }
        }
      }
      updatedAt: string
      sources?: {
        data: {
          sources?: string
        }
      }
      category: {
        value: string
        slug: string
      }
      subcategory: {
        slug: string
      }
      publishedDate: string
      description: string
      slug: string
      wordCount: number
      tags: {
        value: string
      }[]
    }
    allStrapiArticle: {
      nodes: {
        title: string
        slug: string
        description: string
        category: {
          slug: string
        }
        subcategory: {
          slug: string
        }
        primaryImage: {
          localFile: {
            childImageSharp: {
              gatsbyImageData: IGatsbyImageData
            }
          }
        }
      }[]
    }
  }
};

type Position = 'main' | 'right';

const getBroadStreetID = (category: string, subcategory: string, position: Position) => {
  if (position === 'main') {
    if (category !== 'career') return process.env.GATSBY_BROADSTREET_DENTAL_HEALTH_MAIN;
    switch (subcategory) {
      case 'rdh':
        return process.env.GATSBY_BROADSTREET_RDH_MAIN;
      case 'dental-assistant':
        return process.env.GATSBY_BROADSTREET_DENTAL_ASSISTANT_MAIN;
      case 'dentist':
        return process.env.GATSBY_BROADSTREET_DENTIST_MAIN;
      default:
        return process.env.GATSBY_BROADSTREET_RDH_MAIN;
    }
  }
  if (category !== 'career') return process.env.GATSBY_BROADSTREET_DENTAL_HEALTH_RIGHT;
  switch (subcategory) {
    case 'rdh':
      return process.env.GATSBY_BROADSTREET_RDH_RIGHT;
    case 'dental-assistant':
      return process.env.GATSBY_BROADSTREET_DENTAL_ASSISTANT_RIGHT;
    case 'dentist':
      return process.env.GATSBY_BROADSTREET_DENTIST_RIGHT;
    default:
      return process.env.GATSBY_BROADSTREET_RDH_RIGHT;
  }
};

const createKeywordsArray = (
  tags: { value: string }[],
  categorySlug: string,
  subcategorySlug: string,
  slug: string,
) => {
  const keywordsArray = [];
  keywordsArray.push(categorySlug, subcategorySlug, slug);
  if (tags && tags.length > 0) {
    tags.forEach((tag) => keywordsArray.push(tag.value));
  }
  return keywordsArray;
};

const createKeywordsString = (keywordsArray: string[]) => keywordsArray.toString();

const BlogArticle = ({ data, pageContext }: BlogsArticleProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    title, author, reviewer, productList, medicalReviewDate, sponsoredText,
    primaryImage, updatedAt, category, sources, subcategory, tags, slug,
  } = data.strapiArticle;
  const { nodes: relatedArticles } = data.allStrapiArticle;
  const { cites } = pageContext;
  const { content } = pageContext.content.data;
  const [showSources, setShowSources] = useState(false);
  const [curatedContent, setCuratedContent] = useState(content);

  const getTitle = (
    prefix: string | null,
    firstName: string,
    lastName: string,
    suffix: string | null,
  ) => {
    const namePrefix = prefix || '';
    const nameSuffix = suffix || '';
    return `${namePrefix} ${firstName} ${lastName} ${nameSuffix}`;
  };
  const indexedContent = generateIndexedContent({ content });
  const mainImage = primaryImage ? getImage(primaryImage.localFile) : undefined;
  const formattedDate = formatDateToString(updatedAt, 'long');
  const rightAdID = getBroadStreetID(category.slug, subcategory.slug, 'right');
  const mainAdID = getBroadStreetID(category.slug, subcategory.slug, 'main');
  const broadstreetKeywords = createKeywordsArray(tags, category.slug, subcategory.slug, slug);
  const broadstreetKeywordsString = createKeywordsString(broadstreetKeywords);

  useEffect(() => {
    const contentWithHighlight = parseProductHighlight(content, productList);
    if (mainAdID) {
      const contentWithAds = addMainAds(
        contentWithHighlight,
        mainAdID,
        6,
        broadstreetKeywordsString,
      );
      setCuratedContent(contentWithAds);
    } else {
      setCuratedContent(contentWithHighlight);
    }
  }, []);

  useEffect(() => {
    if (isWindowDefined) {
      const figures = Array.from(document.getElementsByTagName('figure'));
      if (figures && figures.length > 0) {
        figures.forEach((figure, index) => {
          const oembed = figure.querySelector('oembed');
          if (oembed) {
            const url = figure.querySelector('oembed')!.getAttribute('url')!;
            const iframe = document.createElement('iframe');
            const youtubeUrl = createYoutubeUrl(url);
            iframe.setAttribute('loading', 'lazy');
            iframe.srcdoc = getVideoSrcDoc(youtubeUrl, getYoutubeId(youtubeUrl));
            iframe.title = `Video ${index}`;
            figure.replaceWith(iframe);
          }
        });
      }
      const images = Array.from(document.getElementsByTagName('img'));
      if (images && images.length > 0) {
        images.forEach((img) => {
          img.loading = 'lazy';
        });
      }
    }
  }, [curatedContent]);

  const handleIndexClick = (index: number) => {
    if (contentRef.current) {
      const targetElement = contentRef.current.querySelectorAll('h2')[index];
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home page', slug: '/' },
    { name: category.value, slug: `/${category.slug}` },
    { name: title, slug: '' },
  ];

  return (
    <Layout>
      <div className={styles.genericContainer}>

        <Breadcrumbs className={styles.breadCrumb} breadcrumbs={breadcrumbs} />

        <div className={styles.gridContent}>
          <div className={styles.indexSectionDesktop}>
            <Indexes title="In This Article" indexes={indexedContent} onIndexClick={handleIndexClick} />
          </div>
          <div className={styles.mainContent}>
            <h1 className={styles.headingTitle}>{title}</h1>
            <Card
              key={author.nameLast}
              className={styles.card}
              image={getImage(author.avatar.localFile)}
              fullName={getTitle(
                author.namePrefix,
                author.nameFirst,
                author.nameLast,
                author.nameSuffix,
              )}
              principalSlug={createAuthorsUrl(author.slug)}
              secondarySlug={reviewer ? createReviewersUrl(reviewer.slug) : undefined}
              reviewer={reviewer ? getTitle(
                reviewer.namePrefix,
                reviewer.nameFirst,
                reviewer.nameLast,
                reviewer.nameSuffix,
              ) : undefined}
              medicalReviewDate={medicalReviewDate}
              type={CardType.mixed}
              sources={!!(sources?.data?.sources || cites.length > 0)}
            />
            {sponsoredText && (
              <div className={styles.sponsorCard}>
                <p dangerouslySetInnerHTML={{ __html: sponsoredText.text.data.text }} />
              </div>
            )}
            <div className={styles.indexSectionMobile}>
              <Indexes title="In This Article" indexes={indexedContent} onIndexClick={handleIndexClick} />
            </div>
            {mainImage && (
              <GatsbyImage
                image={mainImage}
                alt={title}
                className={styles.mainImage}
                objectFit="cover"
              />
            )}
            <div
              ref={contentRef}
              className={styles.contentStyle}
              dangerouslySetInnerHTML={{ __html: curatedContent }}
            />
            <div className={styles.subscribeSection}>
              <StaticImage
                placeholder="blurred"
                layout="constrained"
                src="../../images/tooth.png"
                alt="subscribe"
                className={styles.subscribeImageContainer}
              />
              <div className={styles.subscribeContent}>
                <p className={styles.subscribeTitle}>Make your inbox smile!</p>
                <SmartLink
                  key={pageContext.subscribeBannerName}
                  ariaLabel={pageContext.subscribeBannerName}
                  to={pageContext.subscribeBannerLink}
                  className={styles.buttonStyle}
                >
                  {pageContext.subscribeBannerName}
                </SmartLink>
              </div>
            </div>
            {!!(sources || cites.length > 0) && (
              <div className={showSources ? '' : styles.closedSources}>
                <button
                  className={classnames(styles.sourceButton, styles.expandSectionButton, showSources ? styles.expanded : '')}
                  onClick={() => setShowSources(!showSources)}
                >
                  <h2 className={styles.sourcesTitle}>
                    Sources
                    <Chevron className={classnames(styles.sourcesTitleIcon, showSources ? styles.expanded : '')} />
                  </h2>
                </button>
                {showSources && (
                  <div className={styles.openedSources}>
                    <p className={styles.sorcesContent}>
                      teethtalkgirl content is medically reviewed and fact-checked by a
                      licensed dentist or medical doctor to ensure the information is factual,
                      current, and relevant.
                    </p>
                    <p className={styles.sorcesContent}>
                      Our medical affairs team works hard to ensure the accuracy and integrity
                      by cite from current scientific research, such as scholarly articles,
                      dentistry textbooks, government agencies, and medical journals. This also
                      includes information provided by the American Dental Association (ADA),
                      the American Association of Orthodontics
                      (AAO), and the American Academy of Pediatrics (AAP).
                    </p>
                    {sources?.data?.sources
                      ? (
                        <p
                          className={styles.sorcesContent}
                          dangerouslySetInnerHTML={{ __html: sources?.data.sources || '' }}
                        />
                      )
                      : cites.map((cite) => (
                        <SourceCite cite={cite} className={styles.sorcesContent} />
                      ))}
                  </div>
                )}
              </div>
            )}
            <Card
              key={`${author.nameLast}card`}
              image={getImage(author.avatar.localFile)}
              fullName={getTitle(
                author.namePrefix,
                author.nameFirst,
                author.nameLast,
                author.nameSuffix,
              )}
              principalSlug={createAuthorsUrl(author.slug)}
              shortBio={author.shortDescription}
              type={CardType.author}
              className={styles.card}
            />
            {reviewer && (
              <Card
                key={`${reviewer.nameLast}reviewer`}
                image={getImage(reviewer.avatar.localFile)}
                fullName={getTitle(
                  reviewer.namePrefix,
                  reviewer.nameFirst,
                  reviewer.nameLast,
                  reviewer.nameSuffix,
                )}
                principalSlug={createReviewersUrl(reviewer.slug)}
                shortBio={reviewer.shortDescription}
                type={CardType.reviewer}
                className={styles.card}
              />
            )}
            <div className={styles.lastUpdated}>
              <span>Last updated on</span>
              <span>{formattedDate}</span>
              <Link to="/advertising-policy">Here is our process</Link>
            </div>
          </div>
          {relatedArticles?.length > 0 && (
            <div className={styles.relatedSection}>
              <p className={styles.relatedTitle}>Related Articles</p>
              <div className={styles.relatedItems}>
                {relatedArticles.map((article) => (
                  <RelatedCard
                    title={article.title}
                    description={article.description}
                    slug={createArticleUrl(
                      article.category.slug,
                      article.subcategory.slug,
                      article.slug,
                    )}
                    image={getImage(article.primaryImage.localFile)}
                    className={styles.relatedOnTablet}
                    key={article.slug}
                  />
                ))}
              </div>
              {rightAdID && (
              <div className={styles.rightBroadstreetAd}>
                <broadstreet-zone zone-id={rightAdID} keywords={broadstreetKeywordsString} />
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BlogArticle;

export const query = graphql`
  query ArticlesPage($slug: String, $categoryId: String) {
    strapiArticle(slug: {eq: $slug}) {
      id
      title
      author {
        nameFirst
        nameLast
        namePrefix
        nameSuffix
        slug
        contributorType
        occupation
        shortDescription
        socialMedia {
          instagram
          linkedIn
          pinterest
          tiktok
          twitter
          youtube
          facebook
        }
        avatar {
          url
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      reviewer {
        nameFirst
        nameLast
        namePrefix
        nameSuffix
        slug
        contributorType
        shortDescription
        avatar {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      primaryImage {
        url
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      productList {
        strapi_id
        id
        name
        title
        description
        image {
          url
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        price
        site {
          link
        }
        highlight {
          item
          id
        }
      }
      medicalReviewDate
      sponsoredText {
        text {
          data {
            text
          }
        }
      }
      updatedAt
      category {
        value
        slug
      }
      subcategory {
        slug
      }
      sources {
        data {
          sources
        }
      }
      publishedDate
      description
      slug
      wordCount
      tags {
        value
      }
    }
    allStrapiArticle(
      filter: {category: {id: {eq: $categoryId}}, slug: {ne: $slug}}
      limit: 6
    ) {
      nodes {
        title
        slug
        category {
          slug
        }
        subcategory {
          slug
        }
        description
        primaryImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }
  }
`;

export const Head = ({ data, pageContext }: BlogsArticleProps) => {
  const { strapiArticle } = data;
  const { author } = strapiArticle;
  const articleSlug = createArticleUrl(
    strapiArticle.category.slug,
    strapiArticle.subcategory.slug,
    strapiArticle.slug,
  );
  const articleJSONLD: ArticleSEO = {
    image: strapiArticle.primaryImage?.url || '',
    datePublished: strapiArticle.publishedDate,
    description: strapiArticle.description,
    headline: strapiArticle.title,
    ...(strapiArticle.tags && strapiArticle.tags.length > 0) && { keywords: strapiArticle.tags.map((tag) => tag.value).join(',') },
    url: getAbsoluteUrl(articleSlug),
  };
  const fullName = getFullName(
    author.namePrefix,
    author.nameFirst,
    author.nameLast,
    author.nameSuffix,
  );

  const breadcrumbs: BreadcrumbSEO[] = [
    {
      position: 1,
      name: 'Teeth Talk Girl',
      slug: '',
    },
    {
      position: 2,
      name: strapiArticle.category.value,
      slug: `/${strapiArticle.category.slug}`,
    },
    {
      position: 3,
      name: strapiArticle.title,
      slug: articleSlug,
    },
  ];

  const jsonLDArray = [
    createArticlesJSONLD(
      articleJSONLD,
      fullName,
      createAuthorsUrl(author.slug),
      author.socialMedia,
      author.avatar.url,
      pageContext.content.data.content,
      author.occupation,
      strapiArticle.updatedAt,
      strapiArticle.wordCount,
    ),
    createBreadCrumbJSONLD(breadcrumbs),
  ];

  return (
    <>
      <SEO
        title={strapiArticle.title}
        description={strapiArticle.description}
        ogImageUrl={strapiArticle.primaryImage?.url}
        ogSlug={articleSlug}
        jsonLDArray={jsonLDArray}
      >
        <>
          <meta property="og:type" content="articles" />
          <meta property="article:published_time" content={strapiArticle.publishedDate} />
          {strapiArticle.updatedAt && <meta property="article:modified_time" content={strapiArticle.updatedAt} /> }
          <meta property="article:author" content={fullName} />
          {strapiArticle.tags && strapiArticle.tags.map((tag) => (
            <meta key={tag.value} property="article:tag" content={tag.value} />
          ))}
        </>
      </SEO>
      <Script src="https://cdn.broadstreetads.com/init-2.min.js" async />
      <Script dangerouslySetInnerHTML={{
        __html:
        `
        window.broadstreet = window.broadstreet || { run: [] };
        window.broadstreet.run.push(function() {
          broadstreet.watch()
        });
      `,
      }}
      />
    </>
  );
};
